import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import api from "../../../axiosconfig.js";
import "./LoginPage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faEnvelope,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { useOnboarding } from "../contexts/OnboardingContext";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import {
  loginStart,
  loginSuccess,
  loginFailure
} from "../../../store/authSlice";

export const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [errorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginMode, setLoginMode] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setOnboardingData } = useOnboarding();

  const handleEmailChange = (e) => {
    const emailInput = e.target.value;
    setEmail(emailInput);
    validateForm(emailInput);
  };

  const handlePasswordChange = (e) => {
    const passwordInput = e.target.value;
    setPassword(passwordInput);
    // no need to recompute email validity here
  };

  const validateForm = (emailInput) => {
    const isEmailValid =
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(emailInput);
    setIsFormValid(isEmailValid);
  };

  // Google login
  const handleGoogleLogin = async (credentialResponse) => {
    const userCredentials = jwtDecode(credentialResponse.credential);
    const userEmail = userCredentials.email;

    dispatch(loginStart());
    setIsLoading(true);

    try {
      const response = await api.post("/login", {
        request: "checkGoogle",
        email: userEmail,
      });

      if (!response.data.result) {
        dispatch(
          loginFailure(
            "We could not find a Mindsherpa account associated with this Google account. Please register an account with Mindsherpa."
          )
        );
        toast.error(
          "We could not find a Mindsherpa account associated with this Google account. Please register an account with Mindsherpa."
        );
        navigate("/get-started");
        setIsLoading(false);
        return;
      }

      // Begin session to get tokens
      const responseKey = await api.post("/login", {
        request: "beginLoginSessionGoogle",
        email: userEmail,
      });

      if (responseKey.data.result !== 0) {
        const { access_token, refresh_token, user, registered } =
          responseKey.data;

        dispatch(loginSuccess({ access_token, refresh_token, user }));


        if (registered) {
          setOnboardingData((prev) => ({ ...prev, email: userEmail }));
          navigate("/Onboarding");
        } else {
          navigate("/home");
        }
      }
    } catch (err) {
      dispatch(
        loginFailure(
          "There was an error authenticating your account through Google Sign-In."
        )
      );
      toast.error(
        "There was an error authenticating your account through Google Sign-In."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Email/password login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    dispatch(loginStart());
    setIsLoading(true);

    try {
      const response = await api.post("/login", {
        request: "check",
        email,
        password,
      });

      if (response.data.security) {
        // 2FA path
        setOnboardingData((prev) => ({ ...prev, email }));
        await api.post("/login", { request: "sendtwoFA", email });
        navigate("/TwoFA");
        return;
      }

      if (!response.data.result) {
        dispatch(loginFailure("Invalid email or password. Please try again."));
        toast.error("Invalid email or password. Please try again.");
        return;
      }

      // Get tokens
      const responseKey = await api.post("/login", {
        request: "beginLoginSession",
        email,
        password,
      });

      if (responseKey.data.result !== 0) {
        const { access_token, refresh_token, user, registered } =
          responseKey.data;

        dispatch(loginSuccess({ access_token, refresh_token, user }));

        if (registered) {
          setOnboardingData((prev) => ({ ...prev, email }));
          navigate("/Onboarding");
        } else {
          navigate("/home");
        }
      }
    } catch (err) {
      console.error("Error logging in:", err);
      dispatch(loginFailure("Server error. Please try again later."));
      if (err.response)
        toast.error(
          err.response.data?.message ||
          "Invalid email or password. Please try again."
        );
      else setError("Server error. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-main-page-wrapper">
      <script src="https://accounts.google.com/gsi/client" async></script>
      <div className="login-background-container">
        <div className="login-content-wrapper">
          <div className="login-logo-tagline-container">
            <div className="login-company-logo-container">
              <div className="login-company-name">
                <p>mindsherpa</p>
              </div>
            </div>
            <div className="login-tagline">
              <p>Your space to de-stress, and support each other at work.</p>
            </div>
          </div>

          <div className="login-middle-container">
            <div className="login-headline-subtitle-container">
            <div className="login-dashboard-headline">
              <p>Welcome Back to Mindsherpa</p>
            </div>
            <div className="login-dashboard-subtitle">
              <p>
                Your space for workplace well-being. Let's get you signed in.
              </p>
            </div>
          </div>

          <div className="login-main-form-container">
            <div className="login-email-section">
              {/* Email mode */}
              <form
                onSubmit={handleLogin}
                className="login-main-input-container"
                style={{ display: loginMode === "email" ? "flex" : "none" }}
              >
                <div className="label-wrapper">
                  <label className="label" htmlFor="email">
                    {email ? "Enter Email Address" : "Email Address"}
                  </label>
                </div>

                <div className={`input-wrapper1 ${isLoading ? "disabled-input" : ""}`}>
                  <FontAwesomeIcon icon={faEnvelope} className="input-icon1" />
                  <input
                    type="email"
                    id="email"
                    className="email-input"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="Enter your Email"
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>

                <div className="label-wrapper">
                  <label className="label" htmlFor="password">
                    {password ? "Enter Password" : "Password"}
                  </label>
                </div>

                <div
                  className={`input-wrapper2 ${isLoading ? "disabled-input" : ""}`}
                  style={{ position: "relative" }}
                >
                  <FontAwesomeIcon icon={faLock} className="input-icon2" />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    className="passwords-input"
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder="Enter your Password"
                    disabled={isLoading}
                    aria-label="Password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword((s) => !s)}
                    disabled={isLoading}
                    className="password-visibility-toggle"
                    tabIndex={0}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>

                {error && <p className="error-text">{error}</p>}

                <button
                  type="submit"
                  className={`login-button ${errorMessage ? "with-error" : ""}`}
                  disabled={isLoading || !isFormValid}
                >
                  {isLoading ? (
                    <span className="login-button-content">
                      <span className="login-button-spinner" />
                      Logging in...
                    </span>
                  ) : (
                    "Login"
                  )}
                </button>

                <div
                  className="terms-link try-another-link"
                  onClick={() => !isLoading && setLoginMode("")}
                  style={{ alignSelf: "center", paddingTop: "1rem", cursor: "pointer" }}
                >
                  <p>Try Another Method?</p>
                </div>
              </form>

              {/* Mode chooser */}
              <div
                className="login-main-input-container"
                style={{
                  display: loginMode === "" ? "flex" : "none",
                  width: "300px",
                  alignSelf: "center",
                }}
              >
                <div className="googleLogin">
                  <GoogleLogin
                    width={"300px"}
                    onSuccess={(e) => handleGoogleLogin(e)}
                    onError={() =>
                      toast.error(
                        "There was an error authenticating your account through Google Sign-In."
                      )
                    }
                  />
                </div>

                <button
                  type="button"
                  className="login-email-button"
                  onClick={() => setLoginMode("email")}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                  Sign In With E-mail Address
                </button>
              </div>
            </div>
          </div>
        </div>

          <div className="back-dashboard" onClick={() => navigate(-1)}>
            <div className="back-icon">{"<"}</div>
            <p className="back-text">Back</p>
          </div>

          <div className="login-terms-wrapper">
            <p className="terms-text">
              Don't have an account?
              <a
                href="/get-started"
                className="terms-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/get-started");
                }}
              >
                Sign Up
              </a>
            </p>
            <p className="terms-text">
              Forget your password?
              <a
                href="/ResetPassword"
                className="terms-link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/ResetPassword");
                }}
              >
                Reset Password
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
